// apps/vendor/src/lib/sessionTeardown.ts
//
// Wipe the TanStack Query cache whenever the vendor session ends, so the next
// account never sees the previous vendor's data still cached on screen.
//
// The bug: logout → sign in as a different vendor in the SAME tab → clicking
// around showed the prior account's lists/details until a hard F5. Vendor uses
// the shared `useAuth` store from `@printway/ui`, whose `logout()`
// (packages/ui/src/hooks/useAuth.ts:252-270) ends in `reset()` — that clears
// `user`/`permissions` but never touches the query cache. F5 "fixed" it only
// because a full reload drops the in-memory cache.
//
// Why a store subscription instead of patching the logout button: `user`
// dropping to null is the ONE signal every session-end path shares —
// the explicit logout, the 401 handler after a failed silent refresh, and the
// cross-tab BroadcastChannel logout listener. Hooking the button would miss the
// other two. This mirrors the reasoning in the admin store's clear
// (apps/admin/src/stores/useAuthStore.ts:388-396, dbg-20260720), which puts the
// clear inside the shared `reset()` teardown for the same reason.
//
// admin and merchant-v3 already do this; vendor was the portal left out:
//   • apps/admin/src/stores/useAuthStore.ts:393            (dbg-20260720)
//   • apps/merchant-v3/src/lib/auth/AuthProvider.tsx:177   (dbg-20260602 iter10)

import { useAuth } from '@printway/ui';

import { queryClient } from './queryClient.js';

/** localStorage prefixes that hold auth-scoped data and must not outlive a session. */
const AUTH_SCOPED_STORAGE_PREFIXES = ['vendor.auth.', 'vendor.cache.'];

function clearAuthScopedStorage(): void {
  try {
    if (typeof localStorage === 'undefined') return;
    const toDrop: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && AUTH_SCOPED_STORAGE_PREFIXES.some((p) => key.startsWith(p))) {
        toDrop.push(key);
      }
    }
    for (const key of toDrop) localStorage.removeItem(key);
  } catch {
    /* storage may be unavailable (private mode / quota) — never block teardown */
  }
}

/**
 * Subscribe once, at app boot, to the auth store's `user` slice and clear
 * per-session caches on every transition to a signed-out state.
 *
 * Idempotent: repeat calls are a no-op so an accidental double-mount cannot
 * stack subscriptions.
 */
let wired = false;

export function wireVendorSessionTeardown(): void {
  if (wired) return;
  wired = true;

  useAuth.subscribe(
    (state) => state.user,
    (user, previousUser) => {
      // Only act on the sign-in → sign-out edge. Firing on the initial
      // null → null settle would clear a cache the new session just populated.
      if (user !== null || previousUser === null) return;
      try {
        queryClient.clear();
      } catch {
        /* cache clear is best-effort — never block logout on it */
      }
      clearAuthScopedStorage();
    },
  );
}

export default wireVendorSessionTeardown;
